import { findRow, formatPrice } from "@/data/pricing";
import { pageFaqAdditions } from "@/data/pageFaqs";
import { createFileRoute } from "@tanstack/react-router";
import {
  absoluteUrl,
  breadcrumbSchema,
  canonicalLinks,
  faqPageSchema,
  jsonLd,
  ogImage,
  ogUrl,
  serviceSchema,
  webPageSchema,
} from "@/lib/seo";
import { LocationPage } from "@/components/site/LocationPage";

const answerPrice = (name: string) => formatPrice(findRow(name));

const PATH = "/fractional-coo-newcastle";
const SERVICE_ID = `${absoluteUrl(PATH)}#service`;
const TITLE = "Fractional COO Newcastle | Ajility";
const DESCRIPTION =
  "Fractional COO support for founder-led Newcastle and Hunter businesses doing $3M to $30M. Heavy industry, civil contracting and health.";

const faqs = [
  {
    q: "Do you actually come to Newcastle or is this a remote service?",
    a: "Ajility works on the ground in Newcastle and across the Hunter. A retainer normally runs a full on-site day each week or a two-day block each fortnight, depending on whether the business operates from one site or several. Maitland, Cessnock, Port Stephens and the Upper Hunter are all inside the normal working radius.",
  },
  {
    q: "Is there enough scale in the Hunter for this kind of work?",
    a: "Yes. The Hunter has a large concentration of privately owned businesses between $3M and $30M in mining supply, civil construction, engineering, transport and health. These are exactly the businesses that need an operator and cannot justify a full-time COO on a Sydney salary.",
  },
  {
    q: "What does it cost, and does regional pricing apply?",
    a: "The published catalogue is national. The Operating Diagnostic is from $3,600 plus GST, the Full Diagnostic and 90-Day Roadmap is from $6,900 plus GST for one entity, and most composed retainers for businesses under $15M start from $3,900 per month plus GST. There is no regional discount and no metropolitan loading. The price is set by the deliverable.",
  },
  {
    q: "We supply the mines from Newcastle. Is that different from a Perth mining services business?",
    a: "Commercially it is similar, with the same variation discipline and roster economics. Operationally it differs because Hunter suppliers are usually closer to their customers and carry more coal chain and energy transition exposure, so contract concentration and diversification planning tend to matter more in the conversation.",
  },
  ...pageFaqAdditions["/fractional-coo-newcastle"],
];

export const Route = createFileRoute("/fractional-coo-newcastle")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Fractional COO and GM Newcastle | Ajility" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Fractional COO and GM Newcastle | Ajility" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Fractional COO in Newcastle and the Hunter" },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("fractional-coo-newcastle", "Fractional COO services in Newcastle from Ajility"),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Fractional COO Newcastle", path: PATH }])),
      jsonLd(
        webPageSchema({ image: "fractional-coo-newcastle",
          path: PATH,
          name: TITLE,
          description: DESCRIPTION,
          mainEntityId: SERVICE_ID,
        }),
      ),
      jsonLd({
        ...serviceSchema({
          name: "Fractional COO Newcastle",
          description: DESCRIPTION,
          path: PATH,
          slug: "fractional-coo-newcastle",
          serviceType: "Fractional COO",
          offerId: ["operational-diagnostic", "full-diagnostic-roadmap", "fortnightly-rhythm-base"],
        }),
        areaServed: [
          { "@type": "City", name: "Newcastle" },
          { "@type": "City", name: "Maitland" },
        ],
      }),
      jsonLd(faqPageSchema(PATH, faqs, SERVICE_ID)),
    ],
  }),
  component: NewcastlePage,
});

function NewcastlePage() {
  return (
    <LocationPage
      city="Newcastle"
      region="NSW"
      heading="Fractional COO in Newcastle and the Hunter"
      lead={`Ajility works on site in Newcastle and leads site moves as project and initiative leadership, ${answerPrice("Scoped per initiative")}, with a charter, milestones, weekly governance and a handover to operations. Senior operating leadership for Hunter businesses that build, supply, move and maintain, delivered on site rather than from a Sydney office by email. Most Australian businesses call this seat the general manager; a fractional GM and a fractional COO are the same role.`}
      intro={[
        "The Hunter runs on real assets. Mining supply and maintenance, civil and building contracting, engineering and fabrication, port and freight logistics, defence supply chain around Williamtown, and a large and growing health and aged care sector. The businesses that serve those markets are overwhelmingly privately owned, often second generation, and frequently at the point where the founder is the only person who understands both the operational and the commercial side.",
        "The region also carries a structural conversation that Sydney does not. Energy transition is changing the shape of the customer base for anyone whose revenue has depended on the coal chain, directly or one step removed. Diversification is on the whiteboard in most Hunter boardrooms. Very few of those businesses have translated it into resourced quarterly priorities with named owners, which is why it is still on the whiteboard two years later.",
        "Ajility works with Hunter founders on both problems at once: making the existing operation legible and profitable, and giving the diversification plan somewhere structural to live so it survives a busy quarter.",
      ]}
      industries={[
        {
          name: "Mining supply, maintenance and services",
          note: "Businesses servicing the Upper Hunter operations with labour, maintenance, parts and equipment. Contract concentration is the standing risk and variation discipline is the standing leak. Both are operating rhythm problems before they are commercial ones.",
        },
        {
          name: "Civil and building contracting",
          note: "Civil contractors and builders across Newcastle, Lake Macquarie and Maitland. Job costing that lands weekly, a disciplined variation and claim process, and honest forward resourcing are the three changes that most reliably move net margin.",
        },
        {
          name: "Engineering, fabrication and heavy industry",
          note: "Workshops through Mayfield, Cardiff, Tomago and Beresfield. Quote-to-actual variance, workshop rate recovery and scheduling discipline decide profitability, and most founder-led shops measure none of the three consistently.",
        },
        {
          name: "Port, transport and logistics",
          note: "Operators built around the Port of Newcastle and the regional freight task. Fleet utilisation, maintenance cost per unit and customer-level cost to serve are the operating levers, and the largest customer is very often the least profitable one.",
        },
        {
          name: "Health, aged care and disability services",
          note: "Multi-site providers across Newcastle, Lake Macquarie and Port Stephens working under funded pricing. Roster efficiency, occupancy, clinical admin load and clean claiming are where the margin lives, because the price is set elsewhere.",
        },
        {
          name: "Defence and aerospace supply chain",
          note: "Suppliers into the Williamtown precinct facing compliance, documentation and quality assurance obligations well beyond their size. Structure and documented process are commercial requirements here, not overhead.",
        },
      ]}
      sections={[
        {
          heading: "How Hunter engagements run",
          paragraphs: [
            "Newcastle is inside the normal working radius, so engagements run on site. A retainer is typically a full day each week inside the business, or a two-day block each fortnight where the business operates across several sites and yards. The weekly leadership session is held in person wherever practical, because in an asset-heavy business the walk around the yard before the meeting usually changes what gets discussed in it.",
            "The radius extends to Maitland, Cessnock, Lake Macquarie, Port Stephens and the Upper Hunter. Where crews are dispersed across client sites, part of each visit is spent with supervisors rather than in the office, since the difference between the reported schedule and the real one is visible there and nowhere else.",
            "Between visits the work continues remotely: scorecard maintenance, operating and margin analysis, documentation and one-to-one support for the leaders who own quarterly priorities. Fixed-scope installs, such as a governance rhythm build or a job costing rebuild, run to milestone dates rather than a weekly day, and there are no travel charges within New South Wales; travel time is absorbed in the price.",
          ],
        },
        {
          heading: "What tends to be wrong in a Hunter business",
          paragraphs: [
            "The most common issue is a management layer that was never formally created. The business grew from twelve people to seventy, and the supervisors are the best tradespeople rather than people appointed to a defined role with a scorecard. They are capable and unsupported, so decisions still route to the founder.",
            "The second is contract concentration that is discussed and never structurally addressed. Where one customer is forty per cent of revenue, diversification has to be a resourced quarterly priority with an owner, a milestone and time protected for it. Otherwise it competes with today's delivery and loses every week.",
            "The third is reporting that stops at the company level. Whole-of-business monthly accounts cannot tell a contractor which jobs, crews or customers are carrying the result. Installing job or contract level margin reporting, weekly rather than monthly, is usually the first deliverable of an engagement here.",
          ],
        },
      ]}
      example={{
        label: "Anonymised Hunter example",
        title: "A civil contractor where the good crews were subsidising the rest",
        body: [
          "A Hunter civil contracting business, founder-led, about $14M in revenue, had two strong years followed by a flat one and could not identify what had changed. Everything was reported at company level. Crew and job performance were assessed by reputation, and the reputation was set by who complained least.",
          "The first month put job-level costing in place using existing timesheet and plant data. Once labour, plant and subcontractor cost sat against each job weekly, the picture separated: a third of jobs were performing well above the quoted margin, and a specific work type, won at a rate set three years earlier, was losing money on every job.",
          "The response was to stop quoting that work type at the old rate and to give each supervisor a weekly view of their own job margin. Two supervisors changed their sequencing within a fortnight without being asked, because for the first time they could see the effect. The founder stopped attending job meetings that he had previously chaired out of habit.",
        ],
      }}
      relatedLinks={[
        {
          to: "/services/performance-discipline",
          label: "Performance and Commercial Discipline",
          note: "Job costing, recovery and pricing discipline for Hunter contractors, manufacturers and industrial services businesses.",
        },
        {
          to: "/services/project-leadership",
          label: "Project and Initiative Leadership",
          note: "Leadership on the one initiative that keeps slipping, from site moves to system replacements.",
        },
        {
          to: "/scan",
          label: "Operating Scan",
          note: "A free self-assessment that takes about six minutes and returns where your constraint most likely sits.",
        },
      ]}
      cta={{
        heading: "An operator up the M1",
        body: "Newcastle and the Hunter are close enough for regular days on site rather than occasional visits. Start with a thirty-minute fit call and a plain view of the constraint.",
        linkLabel: "Book a fit call",
      }}
      faqs={faqs}
    />
  );
}
